import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import NumberRating from 'components/Common/NumberRating'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import config from 'constants/config'
import PaletteProvider from 'contexts/PaletteProvider'
import { showUserReviewsRating } from 'lib/reviewUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferTileLoggedInButtons from '../Support/OfferTileLoggedInButtons'
import React, { useContext } from 'react'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import FrontPageTileFlightPricing from '../Support/FrontPageTileFlightPricing'
import HeroOfferTileBundlePricing from '../Support/HeroHotelOfferTileBundlePricing'
import HeroOfferTilePricing from '../Support/HeroHotelOfferTilePricing'
import GeoContext from 'contexts/geoContext'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import Group from 'components/utils/Group'
import VerticalContainerWithFadedOverflow from 'components/Common/VerticalContainerWithFadedOverflow'
import { useTranslation } from 'react-i18next'

const DetailContainer = styled(Pane)`
  width: 100%;
  height: 100%;
  padding-top: ${rem(16)};
  display: flex;

  ${mediaQueryUp.md} {
    flex: 1;
    padding: ${rem(16)} ${rem(32)} ${rem(32)} ${rem(32)};
  }
`

const DetailSegment = styled(Group)`
  position: relative;
  width: 100%;
  flex: 1;
`

const UrgencyLabelsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / 1;

  > * + * {
    margin-left: ${rem(4)};

    ${mediaQueryUp.md} {
      margin-left: ${rem(12)};
    }
  }

  ${mediaQueryUp.md} {
    display: none;
  }
`

const HeaderActions = styled.div`
  display: grid;
  grid-template: "review save";
`

const SaveButtonWrapper = styled.div`
  grid-area: save;
  justify-self: end;
`

const ReviewWrapper = styled.div`
  grid-area: review;
`

const FixedHeightGroup = styled(Group)`
  height: ${rem(212)};
`

interface Props {
  isDesktop: boolean;
  offer: App.Offer | App.OfferSummary;
}

export default function HeroHotelOfferDetails({ isDesktop, offer }: Props) {
  const { currentRegionHasFlights } = useContext(GeoContext)
  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const {
    property,
    offerFlightsEnabled,
    lowestPricePackage,
  } = offer
  const location = offer.type === OFFER_TYPE_TOUR ? offer.location : [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const showReviews = !!offer.property?.rating && showUserReviewsRating(offer.property.rating, offer.property?.reviewsTotal)
  const { t } = useTranslation('common')

  const pricingSection = (
    <>
      {offer.bundledWithFlightsOnly && lowestPricePackage && (
        <HeroOfferTileBundlePricing offer={offer} pkg={lowestPricePackage} />
      )}
      {!offer.bundledWithFlightsOnly && lowestPricePackage && (
        <HeroOfferTilePricing
          offer={offer}
          pkg={lowestPricePackage}
        />
      )}
      {currentRegionHasFlights && offerFlightsEnabled && !offer.bundledWithFlightsOnly &&
        <FrontPageTileFlightPricing offer={offer} />
      }
    </>
  )

  return (
    <PaletteProvider type={isDesktop ? 'default' : 'inverse'}>
      <DetailContainer>
        <Group direction="vertical" gap={16}>
          <TripGuard>
            {isDesktop && (
              <HeaderActions>
                {showReviews && offer.property && (
                  <ReviewWrapper>
                    <NumberRating
                      variant="medium"
                      rating={offer.property.rating}
                      total={offer.property.reviewsTotal}
                      ratingSubtitle={offer.property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined }
                    />
                  </ReviewWrapper>
                )}
                <SaveButtonWrapper>
                  <BookmarkButton offer={offer} />
                </SaveButtonWrapper>
              </HeaderActions>
            )}
          </TripGuard>
          <DetailSegment direction="vertical" gap={16} smGap={24} verticalAlign="space-between">
            <VerticalContainerWithFadedOverflow>
              <FixedHeightGroup direction="vertical" gap={4} horizontalAlign="start">
                <PaletteProvider type="default">
                  <OfferUrgencyLabels offer={offer} />
                </PaletteProvider>
                <Group direction="horizontal" gap={4}>
                  <Caption variant="large" colour="neutral-one">
                    {location}
                  </Caption>
                  <Caption variant="large" colour="neutral-three">
                    ·
                  </Caption>
                  <Caption variant="large" colour="neutral-three">
                    Hotel
                  </Caption>
                </Group>
                <Heading variant="heading5">{property?.name ?? offer.name}</Heading>
                <ProductTypeLabel productType={offer.productType} kind="plain" />
                {!isDesktop && showReviews && offer.property && (
                  <NumberRating
                  inlineLabel
                  variant="xs"
                  rating={offer.property.rating}
                  total={offer.property.reviewsTotal}
                  ratingSubtitle={offer.property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
                />
                )}

                <Group direction="vertical" gap={8}>
                  {property && <BodyText variant="medium" colour="neutral-one" lineClamp={3}>
                    {offer.name}
                  </BodyText>}
                  {(!!offer.badge || offer.bundledWithFlightsOnly) && (
                    <PaletteProvider type="default">
                      <UrgencyLabelsWrapper>
                        {showBadge && offer.badge && <OfferBadgeTag badge={offer.badge} />}
                      </UrgencyLabelsWrapper>
                    </PaletteProvider>
                  )}
                </Group>
              </FixedHeightGroup>
            </VerticalContainerWithFadedOverflow>

            <Group direction="vertical" gap={8}>
              {!offer.walledGarden && pricingSection}
              {offer.walledGarden && (
                <OfferTileLoggedInButtons
                  signUpText="Sign up for free"
                  discountPercentage={Math.round(lowestPricePackage?.discountPercent || 0)}
                  align="none"
                  fullWidth
                >
                  {pricingSection}
                </OfferTileLoggedInButtons>
              )}
            </Group>
          </DetailSegment>
        </Group>
      </DetailContainer>
    </PaletteProvider>
  )
}
